import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Crown, ArrowRight, AlertCircle } from "lucide-react";

export default function SubscriptionSuccess() {
  const { refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");

  const pollStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === "paid" || response.data.payment_status === "complete" || response.data.payment_status === "succeeded") {
        setStatus("success");
        await refreshUser();
        // Track Google Ads conversion for subscription purchase
        if (window.trackConversion) {
          window.trackConversion('subscription_purchase', 75);
        }
        toast.success("Subscription activated successfully!");
      } else if (attempts < 5) {
        setAttempts(prev => prev + 1);
      } else {
        // Try sync endpoint as a fallback before giving up
        try {
          const syncResponse = await axios.post(`${API}/subscriptions/sync`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (syncResponse.data.status === "activated" || syncResponse.data.subscription_status === "active") {
            setStatus("success");
            await refreshUser();
            toast.success("Subscription activated successfully!");
            return;
          }
        } catch (syncErr) {
          // Sync endpoint failed - will set error status below
          toast.error("Payment verification failed. Please contact support.");
        }
        setStatus("error");
      }
    } catch (error) {
      if (attempts < 5) {
        setAttempts(prev => prev + 1);
      } else {
        // Try sync endpoint as a fallback
        try {
          const syncResponse = await axios.post(`${API}/subscriptions/sync`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (syncResponse.data.status === "activated" || syncResponse.data.subscription_status === "active") {
            setStatus("success");
            await refreshUser();
            toast.success("Subscription activated successfully!");
            return;
          }
        } catch (syncErr) {
          // Sync endpoint also failed
          toast.error("Payment verification failed. Please contact support.");
        }
        setStatus("error");
      }
    }
  }, [sessionId, token, attempts, refreshUser]);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const timeoutId = setTimeout(pollStatus, attempts > 0 ? 2000 : 0);
    return () => clearTimeout(timeoutId);
  }, [sessionId, pollStatus, attempts]);

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <main className="pt-24 pb-12 px-4 flex items-center justify-center min-h-screen" data-testid="subscription-success-page">
        <Card className="w-full max-w-md border-stone-200 shadow-lg">
          <CardContent className="py-12 text-center">
            {status === "loading" && (
              <>
                <div className="w-20 h-20 rounded-full bg-forest/10 flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-forest animate-spin" />
                </div>
                <h1 className="font-heading font-bold text-2xl text-gray-900 mb-2">
                  Processing Payment
                </h1>
                <p className="text-gray-600 mb-4">
                  Please wait while we confirm your subscription...
                </p>
                <p className="text-sm text-gray-500">
                  Attempt {attempts + 1} of 5
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="font-heading font-bold text-2xl text-gray-900 mb-2" data-testid="success-title">
                  Welcome to Pro!
                </h1>
                <p className="text-gray-600 mb-6">
                  Your subscription is now active. Start viewing your assigned leads and growing your business.
                </p>
                <div className="flex items-center justify-center gap-2 px-4 py-2 bg-forest/10 text-forest rounded-full mb-8">
                  <Crown className="w-5 h-5" />
                  <span className="font-medium">Pro Member</span>
                </div>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-forest hover:bg-forest-dark rounded-full px-8"
                  data-testid="go-dashboard-btn"
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}

            {status === "error" && (
              <>
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                  <AlertCircle className="w-10 h-10 text-red-600" />
                </div>
                <h1 className="font-heading font-bold text-2xl text-gray-900 mb-2" data-testid="error-title">
                  Something Went Wrong
                </h1>
                <p className="text-gray-600 mb-6">
                  We couldn't confirm your payment. Please contact support if you were charged.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => navigate("/pricing")}
                    variant="outline"
                    className="rounded-full"
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={() => navigate("/pro/dashboard")}
                    className="bg-forest hover:bg-forest-dark rounded-full"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
